import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { calibrationApi, ratingApi, departmentApi, aiApi } from '../../services/api';
import { BandBadge, Spinner, FlagBadge, Modal, EmptyState } from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { FlagIcon, ChevronDownIcon, LockClosedIcon, SparklesIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const BAND_TARGETS = { 'X': { color: 'bg-green-500', label: 'Exceptional' }, 'E+': { color: 'bg-blue-500', label: 'Exceeds' }, 'E': { color: 'bg-gray-400', label: 'Meets' }, 'E-': { color: 'bg-red-500', label: 'Under-performs' } };

function DistributionBar({ dist, targets }) {
  const total = Object.values(dist).reduce((s, v) => s + (v?.count || 0), 0);
  if (!total) return null;
  return (
    <div className="card p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Band distribution</h3>
        <span className="text-xs text-gray-500">{total} employees</span>
      </div>
      <div className="flex h-6 rounded-full overflow-hidden gap-0.5">
        {['X','E+','E','E-'].map(band => {
          const pct = dist[band]?.pct || 0;
          return pct > 0 ? (
            <div key={band} title={`${band}: ${pct}%`} className={clsx(BAND_TARGETS[band]?.color, 'flex items-center justify-center text-white text-xs font-semibold transition-all')} style={{ width: `${pct}%` }}>
              {pct >= 8 ? `${pct}%` : ''}
            </div>
          ) : null;
        })}
      </div>
      <div className="flex gap-4 mt-2 flex-wrap">
        {['X','E+','E','E-'].map(band => (
          <div key={band} className="flex items-center gap-2">
            <BandBadge band={band} />
            <span className="text-xs text-gray-600">{dist[band]?.count || 0} ({dist[band]?.pct || 0}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function OverrideModal({ rating, onClose, onSave, initialBand, initialJust }) {
  const [band, setBand]   = useState(initialBand || rating?.band || 'E');
  const [just, setJust]   = useState(initialJust || '');
  const valid = just.trim().length >= 50;
  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
        Override requires a written justification of at least 50 characters. This action is fully audited.
      </div>
      <div>
        <label className="label">New band</label>
        <div className="flex gap-2">
          {['X','E+','E','E-'].map(b => (
            <button key={b} type="button" onClick={() => setBand(b)}
              className={clsx('px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors', band === b ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}>
              {b}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="label">Justification ({just.length}/50 min)</label>
        <textarea className="input h-24 resize-none" value={just} onChange={e => setJust(e.target.value)} placeholder="Explain why this override is justified based on factors not captured in the scoring model…" />
        <p className={clsx('text-xs mt-1', just.length >= 50 ? 'text-green-600' : 'text-red-500')}>{just.length >= 50 ? '✓ Minimum met' : `${50 - just.length} more characters needed`}</p>
      </div>
      <div className="flex justify-end gap-3">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button disabled={!valid} onClick={() => onSave({ band, justification: just })} className="btn-primary disabled:opacity-40">Apply override</button>
      </div>
    </div>
  );
}

export default function CalibrationBoard() {
  const { cycleId }    = useParams();
  const qc             = useQueryClient();
  const [deptFilter, setDeptFilter] = useState('');
  const [overriding, setOverriding] = useState(null);
  const [overridePrefill, setOverridePrefill] = useState(null);
  const [tab, setTab]               = useState('board'); // board | flags | ai | ninebox

  const { data: boardData, isLoading } = useQuery({
    queryKey:  ['calibration-board', cycleId, deptFilter],
    queryFn:   () => calibrationApi.board(cycleId, deptFilter ? { department_id: deptFilter } : {}).then(r => r.data),
    refetchInterval: 15000,
  });

  const { data: nineBox } = useQuery({
    queryKey:  ['calibration-ninebox', cycleId],
    queryFn:   () => calibrationApi.nineBox(cycleId).then(r => r.data),
    enabled:   tab === 'ninebox',
  });

  const { data: aiSuggestions, isLoading: aiLoading } = useQuery({
    queryKey:  ['ai-calibration-suggestions', cycleId],
    queryFn:   () => aiApi.calibrationSuggestions(cycleId).then(r => r.data),
    enabled:   tab === 'ai',
  });

  const { data: depts } = useQuery({ queryKey: ['departments'], queryFn: () => departmentApi.list().then(r => r.data) });

  const overrideMut = useMutation({
    mutationFn: ({ id, data }) => ratingApi.override(id, data),
    onSuccess:  () => { qc.invalidateQueries(['calibration-board', cycleId]); setOverriding(null); setOverridePrefill(null); toast.success('Override applied and audited'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const lockMut = useMutation({
    mutationFn: () => ratingApi.lock(cycleId),
    onSuccess:  () => { toast.success('All ratings locked'); qc.invalidateQueries(['calibration-board', cycleId]); },
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  const { employees = [], distribution = {}, flags = [] } = boardData || {};
  const errorFlags   = flags.filter(f => f.severity === 'error');
  const warningFlags = flags.filter(f => f.severity === 'warning');

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <Link to="/cycles" className="text-sm text-blue-600 hover:underline">← Cycles</Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">Calibration Board</h1>
        </div>
        <div className="flex gap-2 items-center">
          <select className="input w-44 text-sm" value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
            <option value="">All departments</option>
            {depts?.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <button onClick={() => lockMut.mutate()} className="btn-secondary"><LockClosedIcon className="w-4 h-4"/>Lock all</button>
        </div>
      </div>

      {/* Flags alert */}
      {errorFlags.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3">
          <FlagIcon className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-800">{errorFlags.length} critical flag{errorFlags.length > 1 ? 's' : ''} require attention</p>
            <div className="flex flex-wrap gap-2 mt-2">{errorFlags.slice(0,3).map((f,i) => <FlagBadge key={i} flag={f} />)}</div>
          </div>
        </div>
      )}

      {/* Distribution bar */}
      <DistributionBar dist={distribution} />

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {[['board','Ranked board'],['flags',`Flags (${flags.length})`],['ai','AI Suggestions'],['ninebox','9-box matrix']].map(([key,label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={clsx('px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5', tab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700')}
          >{key === 'ai' && <SparklesIcon className="w-4 h-4" />}{label}</button>
        ))}
      </div>

      {/* Board tab */}
      {tab === 'board' && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {['#','Employee','Role / Band','IP','TP','CP','Final','Rating','Flags','Override'].map(h => <th key={h} className="th">{h}</th>)}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {employees.map(emp => (
                <tr key={emp.id} className={clsx('tr-hover', emp.is_override && 'bg-amber-50')}>
                  <td className="td text-gray-400 text-xs">{emp.rank}</td>
                  <td className="td">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold shrink-0">{emp.employee.name[0]}</div>
                      <div>
                        <Link to={`/employees/${emp.employee_id}`} className="text-sm font-medium text-gray-900 hover:text-blue-600">{emp.employee.name}</Link>
                        <p className="text-xs text-gray-400">{emp.employee.department}</p>
                      </div>
                    </div>
                  </td>
                  <td className="td text-xs capitalize">{emp.employee.role?.replace('_',' ')} / {emp.employee.band}</td>
                  <td className="td text-sm font-mono">{emp.ip_score?.toFixed(1) ?? '—'}</td>
                  <td className="td text-sm font-mono">{emp.tp_score?.toFixed(1) ?? '—'}</td>
                  <td className="td text-sm font-mono">{emp.cp_score?.toFixed(1) ?? '—'}</td>
                  <td className="td"><span className="text-base font-bold text-gray-900">{emp.final_score?.toFixed(1) ?? '—'}</span></td>
                  <td className="td">
                    <div className="flex items-center gap-1">
                      <BandBadge band={emp.band} />
                      {emp.is_override && <span title="Override applied" className="text-amber-500 text-xs">⚡</span>}
                    </div>
                  </td>
                  <td className="td">
                    <div className="flex flex-col gap-1">
                      {(emp.flags || []).slice(0,2).map((f,i) => <FlagBadge key={i} flag={f} />)}
                    </div>
                  </td>
                  <td className="td">
                    {emp.status !== 'locked' && emp.status !== 'published' && (
                      <button onClick={() => setOverriding(emp)} className="text-xs text-blue-600 hover:underline whitespace-nowrap">Override band</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Flags tab */}
      {tab === 'flags' && (
        <div className="space-y-3">
          {flags.length === 0 ? <p className="text-gray-400 text-sm py-8 text-center">No flags detected — clean calibration!</p>
          : flags.map((f, i) => (
            <div key={i} className={clsx('card p-4 border-l-4', f.severity === 'error' ? 'border-red-500 bg-red-50' : f.severity === 'warning' ? 'border-amber-500 bg-amber-50' : 'border-blue-400 bg-blue-50')}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-800">{f.message}</p>
                  {f.employee_name && <p className="text-xs text-gray-500 mt-1">Employee: {f.employee_name} · Score: {f.final_score}</p>}
                </div>
                <FlagBadge flag={f} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AI Suggestions tab */}
      {tab === 'ai' && (
        <div className="space-y-3">
          {aiLoading ? (
            <div className="flex justify-center py-10"><Spinner size="lg" /></div>
          ) : !aiSuggestions || aiSuggestions.length === 0 ? (
            <EmptyState title="No AI suggestions for this cycle" description="The AI layer has no band-change recommendations based on the current signals." />
          ) : (
            aiSuggestions.map((sug, i) => {
              const empObj = employees.find(e => e.employee_id === sug.employee_id || e.id === sug.rating_id);
              const ratingId = sug.rating_id || empObj?.id;
              const currentBand = sug.current_band || empObj?.band || '—';
              const suggestedBand = sug.suggested_band || sug.band;
              const confidence = Math.round((Number(sug.confidence ?? 0)) * (sug.confidence <= 1 ? 100 : 1));
              const signalTexts = (sug.signals || []).map(s => typeof s === 'string' ? s : (s.label || s.type || '')).filter(Boolean);
              const defaultJust = `AI suggestion: final score ${sug.final_score?.toFixed?.(1) ?? sug.score ?? 'N/A'} and historical trend warrant band change from ${currentBand} to ${suggestedBand}. Signals: ${signalTexts.join('; ') || 'performance pattern across cycle'}. Reviewed and endorsed by calibration manager.`;
              return (
                <div key={sug.id || sug.employee_id || i} className="card p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-3 mb-3">
                        <SparklesIcon className="w-5 h-5 text-purple-500" />
                        <p className="font-semibold text-gray-900">{sug.employee?.name || sug.employee_name || empObj?.employee?.name || '—'}</p>
                        <span className="text-xs text-gray-400">{sug.employee?.department || empObj?.employee?.department || ''}</span>
                      </div>
                      <div className="flex items-center gap-3 mb-3">
                        <BandBadge band={currentBand} />
                        <ArrowRightIcon className="w-4 h-4 text-gray-400" />
                        <BandBadge band={suggestedBand} />
                        <span className="text-xs text-gray-500 ml-auto">Confidence</span>
                        <div className="w-28 bg-gray-100 rounded-full h-2">
                          <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${Math.min(100, Math.max(0, confidence))}%` }} />
                        </div>
                        <span className="text-xs font-mono text-gray-700 w-10 text-right">{confidence}%</span>
                      </div>
                      {signalTexts.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {signalTexts.map((s, j) => (
                            <span key={j} className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-gray-50 text-gray-700 border border-gray-200">{s}</span>
                          ))}
                        </div>
                      )}
                      {sug.rationale && <p className="text-sm text-gray-600 mt-2">{sug.rationale}</p>}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <button
                        onClick={() => {
                          const target = empObj || { id: ratingId, employee: { name: sug.employee?.name || sug.employee_name || '' }, band: currentBand };
                          setOverridePrefill({ band: suggestedBand, just: defaultJust });
                          setOverriding(target);
                        }}
                        disabled={!ratingId && !empObj}
                        className="btn-primary text-sm disabled:opacity-40"
                      >
                        Apply suggestion
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* 9-box tab */}
      {tab === 'ninebox' && (
        <div className="card p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-sm font-semibold text-gray-700">9-box talent matrix</p>
              <p className="text-xs text-gray-500 mt-1">Performance (→) vs potential (↑). Click a name to view their profile.</p>
            </div>
            <span className="text-xs text-gray-400">{nineBox?.length ?? 0} employees mapped</span>
          </div>

          {/* Grid — potential (row) × performance (col), y-axis top = high */}
          <div className="flex">
            <div className="flex flex-col justify-between items-center pr-2 py-1 text-xs text-gray-500" style={{ minHeight: 360 }}>
              <span>↑ High</span>
              <span>Potential</span>
              <span>Low ↓</span>
            </div>
            <div className="flex-1 grid grid-cols-3 grid-rows-3 gap-2">
              {[['high','low'], ['high','medium'], ['high','high'],
                ['medium','low'], ['medium','medium'], ['medium','high'],
                ['low','low'], ['low','medium'], ['low','high']].map(([potential, perf]) => {
                const labels = {
                  'high-low': '🌱 Question mark', 'high-medium': '🚀 High potential',  'high-high': '⭐ Star',
                  'medium-low': '📈 Developing',  'medium-medium': '✅ Solid',         'medium-high': '💎 Core performer',
                  'low-low': '⚠️ At risk',       'low-medium': '🔧 Needs coaching',  'low-high': '👀 Watch list',
                }[`${potential}-${perf}`];
                const people = (nineBox || []).filter(n => n.potential === potential && n.performance === perf);
                return (
                  <div key={`${potential}-${perf}`} className="bg-gray-50 border border-gray-200 rounded-xl p-3 min-h-[110px] flex flex-col">
                    <p className="text-xs font-semibold text-gray-700 mb-2">{labels} <span className="text-gray-400">({people.length})</span></p>
                    <div className="flex-1 flex flex-wrap gap-1 content-start">
                      {people.slice(0, 6).map(p => (
                        <Link key={p.employee_id} to={`/app/employees/${p.employee_id}`}
                          title={`${p.employee_name} · ${p.final_score?.toFixed(1)} · ${p.band}`}
                          className="px-2 py-0.5 bg-white border border-gray-200 rounded text-xs text-gray-700 hover:border-blue-300 hover:text-blue-600 truncate max-w-[110px]">
                          {p.employee_name}
                        </Link>
                      ))}
                      {people.length > 6 && <span className="text-xs text-gray-400 self-center">+{people.length - 6} more</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 pl-10 pt-2">
            <span>Low</span><span>Performance →</span><span>High</span>
          </div>
        </div>
      )}

      {/* Override modal */}
      <Modal open={!!overriding} onClose={() => { setOverriding(null); setOverridePrefill(null); }} title={`Override band — ${overriding?.employee?.name}`} size="md">
        {overriding && (
          <OverrideModal rating={overriding}
            initialBand={overridePrefill?.band}
            initialJust={overridePrefill?.just}
            onClose={() => { setOverriding(null); setOverridePrefill(null); }}
            onSave={data => overrideMut.mutate({ id: overriding.id, data })} />
        )}
      </Modal>
    </div>
  );
}
